import React from 'react';
import { Table, Button, Space, Input, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

interface DataTableProps<T> {
  data: T[];
  columns: ColumnsType<T>;
  loading: boolean;
  onEdit: (record: T) => void;
  onDelete: (id: string) => void;
  searchPlaceholder?: string;
}

function DataTable<T extends { id?: string }>({
  data,
  columns,
  loading,
  onEdit,
  onDelete,
  searchPlaceholder = 'Search...'
}: DataTableProps<T>) {
  const [filteredData, setFilteredData] = React.useState<T[]>(data);
  const { t } = useTranslation();

  React.useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const handleSearch = (value: string) => {
    if (!value) {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter((item: any) =>
      String(item.name ?? '').toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const actionColumn = {
    title: t('actions'),
    key: 'actions',
    fixed: 'right' as const,
    width: 150,
    render: (_: any, record: T) => (
      <Space>
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => onEdit(record)}
        >
          {t('edit')}
        </Button>
        <Popconfirm
          title={t('deleteConfirm')}
          onConfirm={() => record.id && onDelete(record.id)}
          okText={t('yes')}
          cancelText={t('no')}
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
          >
            {t('delete')}
          </Button>
        </Popconfirm>
      </Space>
    ),
  };

  const tableColumns = [...columns, actionColumn];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder={searchPlaceholder}
          prefix={<SearchOutlined />}
          allowClear
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
        />
      </div>
      <Table
        columns={tableColumns}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => t('totalItems', { total }),
        }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
}

export default DataTable;
